import { forwardRef, useState, useEffect, useRef, ReactNode, ReactElement, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Badge } from './Badge';

export interface MultiSelectComboBoxOption<T = unknown> {
  value: T;
  label: string;
  disabled?: boolean;
}

const multiSelectComboBoxVariants = cva(
  'flex min-h-10 w-full flex-wrap items-center gap-1 rounded-md border bg-background text-foreground px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-ring focus-within:border-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-colors shadow-input',
  {
    variants: {
      variant: {
        default: 'border-input',
        error: 'border-destructive focus-within:ring-destructive',
      },
      size: {
        default: 'min-h-10',
        sm: 'min-h-8 text-xs',
        lg: 'min-h-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface MultiSelectComboBoxProps<T = unknown>
  extends VariantProps<typeof multiSelectComboBoxVariants> {
  value?: T[];
  onChange: (value: T[]) => void;
  options: MultiSelectComboBoxOption<T>[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  onSearch?: (query: string) => void;
  searchQuery?: string;
  emptyMessage?: string;
  renderOption?: (option: MultiSelectComboBoxOption<T>) => ReactNode;
  renderSelectedValue?: (value: T) => ReactNode;
  className?: string;
  dropdownFooter?: ReactNode;
  getOptionKey?: (option: T) => string | number;
  compareValues?: (a: T | null | undefined, b: T | null | undefined) => boolean;
  maxSelectedDisplay?: number;
}

function MultiSelectComboBoxComponent<T = unknown>(
  {
    value = [],
    onChange,
    options,
    placeholder = 'Select options...',
    error,
    disabled,
    loading,
    searchable = true,
    onSearch,
    searchQuery = '',
    emptyMessage = 'No options found',
    renderOption,
    renderSelectedValue,
    className,
    dropdownFooter,
    variant,
    size,
    getOptionKey,
    compareValues,
    maxSelectedDisplay = 3,
    ...props
  }: MultiSelectComboBoxProps<T>,
  _ref: React.ForwardedRef<HTMLDivElement>
) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  // Generate unique IDs for accessibility
  const comboboxId = useId();
  const listboxId = useId();
  const optionIdPrefix = useId();

  // Type guard to check if object has id property
  const hasIdProperty = (obj: unknown): obj is { id: unknown } => {
    return typeof obj === 'object' && obj !== null && 'id' in obj;
  };

  // Default comparison function for values
  const defaultCompareValues = (a: T | null | undefined, b: T | null | undefined): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;

    // For objects with 'id' property, compare by id
    if (hasIdProperty(a) && hasIdProperty(b)) {
      // Safely access id properties with null checks
      return a.id != null && b.id != null && a.id === b.id;
    }

    return false;
  };

  const compareFunction = compareValues || defaultCompareValues;

  // Note: selectedOptions not used directly but kept for potential future use

  // Use external search query if provided, otherwise use internal
  const currentSearchQuery = onSearch ? searchQuery : internalSearchQuery;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (!onSearch) {
          setInternalSearchQuery('');
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSearch]);

  const handleInputChange = (query: string) => {
    if (onSearch) {
      onSearch(query);
    } else {
      setInternalSearchQuery(query);
    }
    if (!isOpen) setIsOpen(true);
  };

  const handleSelectOption = (option: MultiSelectComboBoxOption<T>) => {
    if (option.disabled) return;

    const isSelected = value.some(val => compareFunction(option.value, val));

    if (isSelected) {
      // Remove from selection
      const newValue = value.filter(val => !compareFunction(option.value, val));
      onChange(newValue);
    } else {
      // Add to selection
      onChange([...value, option.value]);
    }

    // Don't close dropdown for multi-select
    inputRef.current?.focus();
  };

  const handleRemoveValue = (valueToRemove: T) => {
    const newValue = value.filter(val => !compareFunction(valueToRemove, val));
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const filteredOptions = onSearch
      ? options
      : options.filter(option =>
          option.label.toLowerCase().includes(internalSearchQuery.toLowerCase())
        );

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setFocusedOptionIndex(-1);
        if (!onSearch) {
          setInternalSearchQuery('');
        }
        inputRef.current?.blur();
        break;

      case 'Enter':
        e.preventDefault();
        if (isOpen && focusedOptionIndex >= 0) {
          const selectedOption = filteredOptions[focusedOptionIndex];
          if (selectedOption && !selectedOption.disabled) {
            handleSelectOption(selectedOption);
          }
        } else {
          setIsOpen(true);
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedOptionIndex(0);
        } else {
          const nextIndex = Math.min(focusedOptionIndex + 1, filteredOptions.length - 1);
          setFocusedOptionIndex(nextIndex);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          const prevIndex = Math.max(focusedOptionIndex - 1, 0);
          setFocusedOptionIndex(prevIndex);
        }
        break;

      case 'Backspace':
        if (currentSearchQuery === '' && value.length > 0) {
          // Remove the last selected item when backspacing with empty input
          e.preventDefault();
          const newValue = [...value];
          newValue.pop();
          onChange(newValue);
        }
        break;

      case 'Home':
        if (isOpen) {
          e.preventDefault();
          setFocusedOptionIndex(0);
        }
        break;

      case 'End':
        if (isOpen) {
          e.preventDefault();
          setFocusedOptionIndex(filteredOptions.length - 1);
        }
        break;
    }
  };

  // Filter options for internal search
  const filteredOptions = onSearch
    ? options
    : options.filter(option =>
        option.label.toLowerCase().includes(internalSearchQuery.toLowerCase())
      );

  // Render selected values
  const renderSelectedValues = () => {
    if (value.length === 0) return null;

    const displayValues = value.slice(0, maxSelectedDisplay);
    const remainingCount = value.length - maxSelectedDisplay;

    return (
      <div className="flex flex-wrap items-center gap-1">
        {displayValues.map((val, index) => {
          const option = options.find(opt => compareFunction(opt.value, val));
          const displayLabel = renderSelectedValue
            ? renderSelectedValue(val)
            : option?.label || String(val);
          const key = getOptionKey ? getOptionKey(val) : index;

          return (
            <Badge key={key} variant="secondary" className="flex items-center gap-1 pr-1">
              <span className="text-xs">{displayLabel}</span>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveValue(val);
                }}
                className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                aria-label={`Remove ${displayLabel}`}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </Badge>
          );
        })}
        {remainingCount > 0 && (
          <Badge variant="outline" className="text-xs">
            +{remainingCount} more
          </Badge>
        )}
      </div>
    );
  };

  return (
    <div className={cn('relative w-full', className)} ref={dropdownRef} {...props}>
      {/* Input Container */}
      <div className="relative">
        <div
          className={cn(
            multiSelectComboBoxVariants({
              variant: error ? 'error' : variant,
              size,
            }),
            'cursor-text'
          )}
          onClick={() => {
            if (!disabled) {
              setIsOpen(true);
              inputRef.current?.focus();
            }
          }}
        >
          {renderSelectedValues()}

          {searchable && (
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={isOpen ? listboxId : undefined}
              aria-activedescendant={
                focusedOptionIndex >= 0 ? `${optionIdPrefix}-${focusedOptionIndex}` : undefined
              }
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? `${comboboxId}-error` : undefined}
              className="flex-1 min-w-0 bg-transparent border-none outline-none placeholder:text-muted-foreground"
              placeholder={value.length === 0 ? placeholder : ''}
              value={currentSearchQuery}
              onChange={e => handleInputChange(e.target.value)}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
          )}

          <button
            type="button"
            className="ml-2 flex items-center justify-center p-0.5"
            onClick={e => {
              e.stopPropagation();
              if (!disabled) setIsOpen(!isOpen);
            }}
            disabled={disabled}
            aria-label={isOpen ? 'Close options list' : 'Open options list'}
            tabIndex={-1}
          >
            <ChevronDownIcon
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <p id={`${comboboxId}-error`} className="mt-1 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label="Options"
          aria-multiselectable="true"
          className="absolute z-10 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {loading ? (
            <div
              className="px-3 py-2 text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              Loading...
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground" role="status">
              {emptyMessage}
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = value.some(val => compareFunction(option.value, val));
              const optionKey = getOptionKey ? getOptionKey(option.value) : index;

              return (
                <div
                  key={optionKey}
                  id={`${optionIdPrefix}-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled}
                  className={cn(
                    'px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none flex items-center justify-between',
                    option.disabled && 'opacity-50 cursor-not-allowed',
                    isSelected && 'bg-primary/10 text-primary',
                    focusedOptionIndex === index && 'bg-accent text-accent-foreground'
                  )}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setFocusedOptionIndex(index)}
                >
                  <span>{renderOption ? renderOption(option) : option.label}</span>
                  {isSelected && (
                    <div className="ml-2 h-4 w-4 flex items-center justify-center">
                      <div className="h-2 w-2 bg-current rounded-full" />
                    </div>
                  )}
                </div>
              );
            })
          )}
          {dropdownFooter && <div className="border-t border-border">{dropdownFooter}</div>}
        </div>
      )}
    </div>
  );
}

const MultiSelectComboBox = forwardRef(MultiSelectComboBoxComponent) as <T = unknown>(
  props: MultiSelectComboBoxProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReactElement | null;

export { MultiSelectComboBox };
